// Language and theme (under More), and the static texts in index.html.
import type { LangCode, TextKey } from "../shared/strings.ts";
import { $, $maybe, el } from "./dom.ts";
import { LANG, languages, setLangCode, t } from "./i18n.ts";
import { render } from "./state.ts";
import { lsGet, lsSet } from "./util.ts";
import { showTitle } from "./views.ts";
import { storageInfo } from "./backup.ts";
import { pushProfileChanged } from "./push.ts";

/** Fills in index.html's fixed texts: data-i18n (text), data-i18n-ph (placeholder), data-i18n-aria (aria-label). */
export function applyStatic(): void {
  document.documentElement.lang = LANG;
  document.title = t("appName");
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((n) => { n.textContent = t(n.dataset.i18n as TextKey); });
  document.querySelectorAll<HTMLInputElement>("[data-i18n-ph]").forEach((n) => { n.placeholder = t(n.dataset.i18nPh as TextKey); });
  document.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((n) => { n.setAttribute("aria-label", t(n.dataset.i18nAria as TextKey)); });
  $("#langs").replaceChildren(...languages().map(([code, name]) => el("button", { class: "chip", type: "button", lang: code,
    "aria-pressed": String(code === LANG), onclick: () => setLang(code) }, name)));
}

function setLang(code: LangCode): void {
  if (code === LANG) return;
  setLangCode(code);
  setTimeout(pushProfileChanged, 0); // evening tips in the new language
  applyStatic(); applyTheme(); render(); showTitle();
  void storageInfo();
}

/* ---------- theme: follow the phone, or always light / always dark ---------- */
const THEMES = ["auto", "light", "dark"] as const;
type Theme = (typeof THEMES)[number];
const dark = matchMedia("(prefers-color-scheme: dark)");
const theme = (): Theme => { const v = lsGet("theme", "auto"); return v === "light" || v === "dark" ? v : "auto"; };

export function applyTheme(): void {
  const th = theme(), root = document.documentElement;
  if (th === "auto") root.removeAttribute("data-theme"); else root.setAttribute("data-theme", th);
  const isDark = th === "dark" || (th === "auto" && dark.matches);
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => { m.removeAttribute("media"); m.content = isDark ? "#0e1310" : "#2f5d3a"; });
  $maybe("#themes")?.replaceChildren(...THEMES.map((k) => el("button", { class: "chip", type: "button", "aria-pressed": String(th === k),
    onclick: () => { lsSet("theme", k); applyTheme(); } }, t(`theme_${k}`))));
}
dark.addEventListener("change", () => { if (theme() === "auto") applyTheme(); });
